import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

const DAY_LABELS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

interface OperatingHoursSettingsProps {
    restaurantId: string;
}

export function OperatingHoursSettings({ restaurantId }: OperatingHoursSettingsProps) {
    const { toast } = useToast();
    const [settingsForm, setSettingsForm] = useState<any>(null);

    const { data: settings, isLoading } = useQuery<any>({
        queryKey: [`/api/restaurant/${restaurantId}/reservation-settings`],
    });

    useEffect(() => {
        if (settings) {
            setSettingsForm(settings);
        } else if (!isLoading) {
            // Provide a default structure if no settings exist yet
            setSettingsForm({
                restaurantId,
                operatingHours: DAY_LABELS.map((_, i) => ({
                    day: i,
                    open: "09:00",
                    close: "22:00",
                    closed: false
                }))
            });
        }
    }, [settings, isLoading, restaurantId]);

    const updateSettingsMutation = useMutation({
        mutationFn: async (data: any) => {
            const res = await apiRequest("PUT", `/api/restaurant/${restaurantId}/reservation-settings`, data);
            return res.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`/api/restaurant/${restaurantId}/reservation-settings`] });
            toast({
                title: "Success",
                description: "Operating hours updated successfully",
            });
        },
        onError: (error: Error) => {
            toast({
                title: "Error",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    if (isLoading || !settingsForm) {
        return (
            <Card className="p-6">
                <div className="flex items-center justify-center p-8">
                    <Loader2 className="h-8 w-8 animate-spin" />
                </div>
            </Card>
        );
    }

    const hours = (settingsForm.operatingHours as any[]) || [];

    return (
        <Card className="p-6 space-y-4">
            <h3 className="font-semibold text-lg">Operating Hours</h3>
            <p className="text-sm text-muted-foreground">Set when reservations can be booked</p>

            <div className="space-y-3">
                {DAY_LABELS.map((dayName, dayIndex) => {
                    const dayHours = hours.find((h: any) => h.day === dayIndex) || { day: dayIndex, open: "11:00", close: "22:00", closed: false };

                    return (
                        <div key={dayIndex} className="flex items-center gap-3">
                            <span className="w-24 text-sm font-medium">{dayName}</span>
                            <Switch
                                checked={!dayHours.closed}
                                onCheckedChange={(open) => {
                                    const newHours = [...hours.filter((h: any) => h.day !== dayIndex), {
                                        ...dayHours,
                                        closed: !open,
                                    }].sort((a: any, b: any) => a.day - b.day);
                                    setSettingsForm({ ...settingsForm, operatingHours: newHours as any });
                                }}
                            />
                            {!dayHours.closed ? (
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="time"
                                        value={dayHours.open}
                                        onChange={(e) => {
                                            const newHours = [...hours.filter((h: any) => h.day !== dayIndex), {
                                                ...dayHours,
                                                open: e.target.value,
                                            }].sort((a: any, b: any) => a.day - b.day);
                                            setSettingsForm({ ...settingsForm, operatingHours: newHours as any });
                                        }}
                                        className="w-32"
                                    />
                                    <span className="text-muted-foreground">to</span>
                                    <Input
                                        type="time"
                                        value={dayHours.close}
                                        onChange={(e) => {
                                            const newHours = [...hours.filter((h: any) => h.day !== dayIndex), {
                                                ...dayHours,
                                                close: e.target.value,
                                            }].sort((a: any, b: any) => a.day - b.day);
                                            setSettingsForm({ ...settingsForm, operatingHours: newHours as any });
                                        }}
                                        className="w-32"
                                    />
                                </div>
                            ) : (
                                <span className="text-sm text-muted-foreground">Closed</span>
                            )}
                        </div>
                    );
                })}
            </div>

            <Button
                onClick={() => updateSettingsMutation.mutate(settingsForm)}
                disabled={updateSettingsMutation.isPending}
                className="mt-4"
            >
                {updateSettingsMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Save Operating Hours
            </Button>
        </Card>
    );
}
